import { HttpClient, provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import {
  afterNextRender,
  ApplicationConfig,
  DOCUMENT,
  inject,
  makeStateKey,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
  REQUEST,
  TransferState,
} from '@angular/core';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideNgIconLoader } from '@ng-icons/core';
import { popperVariation, provideTippyConfig, provideTippyLoader, tooltipVariation } from '@ngneat/helipopper/config';
import { CookieBannerStore } from '@notfall/cookie-banner';
import { BASE_PATH } from '@notfall/data-access-backend';
import { authInterceptor, languageInterceptor, Path } from '@notfall/feat-core';
import {
  APP_LANGUAGE,
  DEFAULT_LANGUAGE,
  LanguageStore,
  provideAppTransloco,
  provideI18nTitleStrategy,
  TranslocoMultiLoader,
} from '@notfall/util-angular';
import { environment } from '@notfall/util-ts';
import tippy from 'tippy.js';
import { appRoutes } from './app.routes';

export const appConfig: ApplicationConfig = {
  providers: [
    provideClientHydration(withEventReplay()),
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(appRoutes, withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })),
    provideAppTransloco(TranslocoMultiLoader),
    provideNgIconLoader(name => {
      const http = inject(HttpClient);
      return http.get(`/icons/${name}.svg`, { responseType: 'text' });
    }),
    provideTippyLoader(() => tippy),
    provideTippyConfig({
      defaultVariation: 'tooltip',
      delay: 0,
      duration: 0,
      variations: {
        tooltip: tooltipVariation,
        popper: popperVariation,
      },
    }),
    {
      provide: BASE_PATH,
      useValue: '/formservice/services/rest',
    },
    {
      provide: APP_LANGUAGE,
      useFactory: () => {
        const window = inject(DOCUMENT).defaultView;
        const host = window?.location.host ?? '';

        const languageMap = environment.hostLanguageMap;
        const find = Object.keys(languageMap).find(key => key.includes(host));

        if (find && find in languageMap) {
          return languageMap[find as keyof typeof languageMap];
        }

        return DEFAULT_LANGUAGE;
      },
    },

    provideHttpClient(withInterceptors([languageInterceptor, authInterceptor]), withFetch()),
    provideAppInitializer(() => appInitializer()()),
    provideI18nTitleStrategy(),
  ],
};

function appInitializer(languageStore = inject(LanguageStore), cookieBannerStore = inject(CookieBannerStore)) {
  // This part runs on the server side.
  const req = inject(REQUEST, { optional: true });
  const transferState = inject(TransferState);
  const document = inject(DOCUMENT);
  const key = makeStateKey<string>('showBanner');

  if (req) {
    const url = new URL(req.url);

    if (url.searchParams.get('embed') !== 'true') {
      transferState.set(key, 'true');
    }
  }

  return async () => {
    // Some things can only run on the client.
    afterNextRender(() => {
      const path = document.defaultView?.location.pathname ?? '';
      if (!path.startsWith(`/${Path.OCCUPANCIES}`)) {
        languageStore.init();
      }

      const isShowBanner = transferState.get(key, 'false');
      if (isShowBanner === 'true') {
        cookieBannerStore.init();
      }
    });
  };
}
